import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

const STORAGE_KEY = 'kiln:preferences';

interface Preferences {
  buildsStatusFilter: string;
  buildsMineOnly: boolean;
}

const defaultPreferences: Preferences = {
  buildsStatusFilter: '',
  buildsMineOnly: false,
};

function loadPreferences(): Preferences {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...defaultPreferences, ...JSON.parse(stored) };
    }
  } catch {
    // Ignore parse errors
  }
  return { ...defaultPreferences };
}

function savePreferences(prefs: Preferences): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Ignore storage errors (e.g., quota exceeded)
  }
}

export const usePreferencesStore = defineStore('preferences', () => {
  const preferences = ref<Preferences>(loadPreferences());

  // Auto-save when preferences change
  watch(preferences, (newPrefs) => {
    savePreferences(newPrefs);
  }, { deep: true });

  // Builds status filter
  const buildsStatusFilter = ref(preferences.value.buildsStatusFilter);

  watch(buildsStatusFilter, (newValue) => {
    preferences.value.buildsStatusFilter = newValue;
  });

  function setBuildsStatusFilter(filter: string) {
    buildsStatusFilter.value = filter;
  }

  // Builds "only my builds" filter
  const buildsMineOnly = ref(preferences.value.buildsMineOnly);

  watch(buildsMineOnly, (newValue) => {
    preferences.value.buildsMineOnly = newValue;
  });

  function setBuildsMineOnly(mineOnly: boolean) {
    buildsMineOnly.value = mineOnly;
  }

  return {
    buildsStatusFilter,
    setBuildsStatusFilter,
    buildsMineOnly,
    setBuildsMineOnly,
  };
});
